import { toValidatedSolanaAddress, useLogOnce } from '@funkit/connect-core'
import {
  type ValidatedSolanaWalletSigner,
  createSolanaWithdrawalClient,
} from '@funkit/connect-core/clients/solana'
import { useMemo } from 'react'

import type { WithdrawalFlowConfig } from '../../src/flow/withdrawalFlowConfig'
import { logger } from '../../src/logging/logger'
import {
  type SolanaHostWithdrawalConfig,
  type SolanaWithdrawalFlowConfig,
  buildSolanaWithdrawalConfig,
} from './solanaWithdrawal'

/**
 * The config to hand `beginWithdrawal` for a USDC-on-Solana source. Mirrors
 * web's `useSolanaWithdrawalConfig`: `undefined` while the signer is absent,
 * cannot sign, or its address is not base58, so a bad host value disables the
 * flow instead of throwing in render.
 */
export function useSolanaWithdrawal({
  solana,
  config,
}: {
  solana: SolanaWithdrawalFlowConfig | undefined
  config: SolanaHostWithdrawalConfig | undefined
}): WithdrawalFlowConfig | undefined {
  const signer = solana?.signer
  const address = toValidatedSolanaAddress(signer?.address)
  const canSign = Boolean(
    signer?.signTransaction ?? signer?.signAndSendTransaction,
  )

  // Length only: a mis-wired field could hold key material. A signer with no
  // address yet is a wallet mid-connect, not a host bug.
  useLogOnce(
    signer?.address && !address ? String(signer.address.length) : undefined,
    () => {
      logger.warn('connect-rn:solana-withdrawal:invalid_address', {
        addressLength: signer?.address?.length ?? 0,
      })
    },
  )
  useLogOnce(address && !canSign ? address : undefined, () => {
    logger.warn('connect-rn:solana-withdrawal:no_sign_method', { address })
  })
  // Source decision point: which chain and wallet the withdrawal draws from.
  useLogOnce(address && canSign ? address : undefined, () => {
    logger.info('connect-rn:solana-withdrawal:source_selected', { address })
  })

  // Keyed on the signer alone so a new host config object does not rebuild
  // the relay wallet. The host's own object, not a copy: a class-based signer
  // keeps its prototype methods and `this`.
  const withdrawalWallet = useMemo(() => {
    if (!signer || !address || !canSign) {
      return undefined
    }
    return createSolanaWithdrawalClient(
      signer as ValidatedSolanaWalletSigner,
      logger,
    )
  }, [signer, address, canSign])

  return useMemo(() => {
    if (!withdrawalWallet || !config) {
      return undefined
    }
    return buildSolanaWithdrawalConfig({ config, withdrawalWallet })
  }, [withdrawalWallet, config])
}
